import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { Resources } from "../utils/resources";
import { format, subYears, parse } from "date-fns";
import api from "../utils/api";
import { UIUtils } from "../utils/uiUtils";
import { useUser } from "../contexts/UserContext";
import { handleBenefitsNavigation } from "../utils/navigationUtils";
import Loader from "../components/Loader";
import DateInput from "../components/DateInput";
import { Button } from "../components/Button";
import backArrowIcon from "../assets/icons/back-arrow-icon.svg";

interface FormData {
  marital_status: string;
  dob: string;
  childrenNum: string;
  type_of_housing: string;
  income_amount: string;
  marketing_email_optin_flag: boolean;
  marketing_sms_optin_flag: boolean;
  privacy_consent: boolean;
}

interface FormErrors {
  [key: string]: string | undefined; // Add an index signature
  marital_status?: string;
  dob?: string;
  childrenNum?: string;
  type_of_housing?: string;
  income_amount?: string;
  privacy_consent?: string;
}

const GettingStarted: React.FC = () => {
  const navigate = useNavigate();
  const { setIsVisible } = useBottomNavBar();
  const [isLoading, setIsLoading] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState("");
  const {
    user,
    userGoal,
    benefitsSummary,
    updateUserData,
    marketing_email_dnc_flag,
    marketing_sms_dnc_flag,
  } = useUser();

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, [setIsVisible]);

  useEffect(() => {
    // Check if we already have benefits data
    if (benefitsSummary) {
      handleBenefitsNavigation(benefitsSummary, navigate);
    }
  }, [benefitsSummary, navigate]);

  const [formData, setFormData] = useState<FormData>({
    marital_status: "",
    dob: "",
    childrenNum: "",
    type_of_housing: "",
    income_amount: "",
    marketing_email_optin_flag: false,
    marketing_sms_optin_flag: false,
    privacy_consent: true, // Set to true by default since we're removing the UI
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.marital_status) {
      newErrors.marital_status = "Please select your marital status";
    }
    if (!formData.dob) {
      newErrors.dob = "Please enter a valid date of birth";
    }
    if (!formData.childrenNum) {
      newErrors.childrenNum = "Please select the number of dependent children";
    }
    if (!formData.type_of_housing) {
      newErrors.type_of_housing = "Please select your type of housing";
    }
    if (!formData.income_amount) {
      newErrors.income_amount = "Please enter your income amount";
    } else if (
      isNaN(Number(formData.income_amount)) ||
      Number(formData.income_amount) < 0 ||
      Number(formData.income_amount) > 15000
    ) {
      newErrors.income_amount = "Please enter a value between 0 and 15000";
    }
    // Loop through the newErrors object
    for (const key in newErrors) {
      if (newErrors.hasOwnProperty(key)) {
        const errormsg = newErrors[key];
        if (errormsg) {
          // Add tracking in New Relic
          UIUtils.trackFormSubmitErrors(key, errormsg);
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      UIUtils.trackFormSubmit("0", "error");
      return;
    }

    setIsLoading(true);
    setLoaderMessage("We're estimating the benefits you could be entitled to.");

    try {
      // Convert date from dd/MM/yyyy to yyyy-MM-dd for API
      const parsedDate = parse(formData.dob, "dd/MM/yyyy", new Date());
      const formattedDate = format(parsedDate, "yyyy-MM-dd");

      // Prepare API data - invert marketing_email_optin_flag to marketing_email_dnc_flag
      const { marketing_email_optin_flag, ...restFormData } = formData;
      const formDataWithEmail = {
        ...restFormData,
        dob: formattedDate,
        email: user?.email || "",
        marketing_email_dnc_flag: !marketing_email_optin_flag,
      };
      UIUtils.trackFormSubmit("0", "success");
      const lsrc = UIUtils.getCookieValue("lsrc") || "default";
      const response = await api.postShortForm(formDataWithEmail, lsrc);
      if (response.status === 200) {
        // Add stage: 'estimate' to benefits_summary before updating user data
        const benefitsSummaryWithStage = {
          ...response.data,
          stage: "estimate",
        };

        // Update user data with new benefits information while preserving other data
        updateUserData({
          user: user,
          userGoal: userGoal, // Preserve existing goal data
          benefitsSummary: benefitsSummaryWithStage,
          benefitsBreakdown: response.data.benefits_breakdown,
          marketing_email_dnc_flag: !formData.marketing_email_optin_flag,
          marketing_sms_dnc_flag: !formData.marketing_sms_optin_flag,
        });

        // Navigate based on benefits stage
        handleBenefitsNavigation(benefitsSummaryWithStage, navigate);
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      UIUtils.trackException("Short benefits submit", error);
      UIUtils.showMessageOnSnackBar(
        "Something went wrong. Please try again later.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setFormData((prev) => ({
        ...prev,
        dob: format(date, "dd/MM/yyyy"),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        dob: "",
      }));
    }
  };

  if (isLoading) {
    return <Loader message={loaderMessage} />;
  }

  const selectedDate = formData.dob
    ? parse(formData.dob, "dd/MM/yyyy", new Date())
    : null;

  return (
    <div className="content-container">
      <div className="flex items-center justify-between pb-4">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center mr-4"
            aria-label="Go back"
          >
            <img src={backArrowIcon} alt="back" />
          </button>
          <h1 className="text-3xl">Use our BenefitGlance tool</h1>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow container mx-auto">
        <div className="mb-4">
          <p className="font-bold mb-4">
            We need a few details to see if you could be entitled to more
            government benefits.
          </p>
          <p>
            Answer the questions below to see how much extra money you could get
            each month.
          </p>
        </div>

        <div className="container-white-rounded">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Marital Status */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Marital status
              </label>
              <select
                value={formData.marital_status}
                name="martial_status"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    marital_status: e.target.value,
                  }))
                }
                className="form-input-secondary"
              >
                <option value="">Select an option</option>
                {Object.entries(Resources.maritalStatusOptions).map(
                  ([label, value]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ),
                )}
              </select>
              {errors.marital_status && (
                <p className="text-red text-sm mt-1">{errors.marital_status}</p>
              )}
            </div>

            {/* Date of Birth */}
            <div className="space-y-2">
              <DateInput
                dataSensitive="true"
                selected={selectedDate}
                onChange={handleDateChange}
                name="dob"
                maxDate={subYears(new Date(), 18)}
                label="Date of Birth"
                error={errors.dob}
                // className="form-input-secondary"
                placeholderText="DD/MM/YYYY"
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                dateFormat="dd/MM/yyyy" // Use slashes for non-loan application
              />
            </div>

            {/* Number of Children */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Number of dependent children
              </label>
              <select
                value={formData.childrenNum}
                name="childrenNum"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    childrenNum: e.target.value,
                  }))
                }
                className="form-input-secondary"
              >
                <option value="">Select an option</option>
                {Object.entries(Resources.numberOfChildrenOptions).map(
                  ([label, value]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ),
                )}
              </select>
              {errors.childrenNum && (
                <p className="text-red text-sm mt-1">{errors.childrenNum}</p>
              )}
            </div>

            {/* Housing Type */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">Are you...</label>
              <select
                value={formData.type_of_housing}
                name="type_of_housing"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    type_of_housing: e.target.value,
                  }))
                }
                className="form-input-secondary"
              >
                <option value="">Select an option</option>
                {Object.entries(Resources.housingTypeOptions).map(
                  ([label, value]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ),
                )}
              </select>
              {errors.type_of_housing && (
                <p className="text-red text-sm mt-1">
                  {errors.type_of_housing}
                </p>
              )}
            </div>

            {/* Monthly Income */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Monthly income (after tax)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-4">£</span>
                <input
                  type="number"
                  name="income_amount"
                  value={formData.income_amount}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      income_amount: e.target.value,
                    }))
                  }
                  className="form-input-secondary pl-8"
                  placeholder="Enter amount"
                />
              </div>
              {errors.income_amount && (
                <p className="text-red text-sm mt-1">{errors.income_amount}</p>
              )}
            </div>

            {/* Marketing Opt-in - Only show if user hasn't opted in for email or SMS */}
            {(marketing_email_dnc_flag || marketing_sms_dnc_flag) && (
              <div className="flex items-start space-x-2">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="text-sm">
                  <strong className="text-secondary">Marketing communications from Afforda</strong>
                  <p className="mt-1 text-xs text-gray-600">
                    Gain Credit has a range of products and brands. These include Afforda. We want to share news and deals from all of them. We may also share offers from some brands we trust. You can unsubscribe at any time in your profile settings. Select how you'd like to hear from us:
                  </p>
                  <div className="space-y-3 mt-2">
                    {marketing_email_dnc_flag && (
                      <div className="flex items-start">
                        <div className="flex h-5 items-center">
                          <input
                            type="checkbox"
                            id="marketing_email_optin_flag"
                            checked={formData.marketing_email_optin_flag}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                marketing_email_optin_flag: e.target.checked,
                              }))
                            }
                            className="form-checkbox"
                          />
                        </div>
                        <label
                          htmlFor="marketing_email_optin_flag"
                          className="ml-2 text-sm text-secondary"
                        >
                          Email
                        </label>
                      </div>
                    )}
                    {marketing_sms_dnc_flag && (
                      <div className="flex items-start">
                        <div className="flex h-5 items-center">
                          <input
                            type="checkbox"
                            id="marketing_sms_optin_flag"
                            checked={formData.marketing_sms_optin_flag}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                marketing_sms_optin_flag: e.target.checked,
                              }))
                            }
                            className="form-checkbox"
                          />
                        </div>
                        <label
                          htmlFor="marketing_sms_optin_flag"
                          className="ml-2 text-sm text-secondary"
                        >
                          SMS
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <Button
              text="Submit"
              onClick={() => {}}
              type="submit"
              className="w-full"
              variant="secondary"
            />
          </form>
        </div>
      </main>
    </div>
  );
};

export default GettingStarted;
